import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function CaptionGenerator() {
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState('professional');
  const [platform, setPlatform] = useState('instagram');
  const [captions, setCaptions] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);

  const tones = [
    { value: 'professional', label: 'Professional', icon: 'ri-briefcase-line' },
    { value: 'casual', label: 'Casual', icon: 'ri-chat-smile-line' },
    { value: 'funny', label: 'Funny', icon: 'ri-emotion-laugh-line' },
    { value: 'inspirational', label: 'Inspirational', icon: 'ri-lightbulb-line' },
  ];

  const platforms = [
    { value: 'instagram', label: 'Instagram', icon: 'ri-instagram-line' },
    { value: 'twitter', label: 'Twitter', icon: 'ri-twitter-line' },
    { value: 'facebook', label: 'Facebook', icon: 'ri-facebook-line' },
    { value: 'linkedin', label: 'LinkedIn', icon: 'ri-linkedin-line' },
  ];

  // Simulated AI caption generation
  const generateCaptions = () => {
    if (!topic.trim()) return;

    setIsGenerating(true);
    setCaptions([]);

    // Simulate API call
    setTimeout(() => {
      const templates = {
        professional: [
          `${topic} - Driving excellence and innovation in everything we do.`,
          `Proud to share our latest work on ${topic}. Here's what we learned.`,
          `${topic}: Quality, precision, and dedication come together.`,
          `Taking ${topic} to the next level. Excited to share this journey with you.`,
          `${topic} done right. Commitment to excellence every single day.`,
        ],
        casual: [
          `Just ${topic} things ✨`,
          `Weekend vibes: ${topic} edition 🌟`,
          `Can't get enough of ${topic} lately!`,
          `${topic} hits different when you love what you do 💙`,
          `Living my best life with ${topic} 🎉`,
        ],
        funny: [
          `${topic}: Because adulting is hard and we all need this 😂`,
          `Me trying ${topic} vs. me actually doing ${topic} 💀`,
          `${topic} got me like... *chef's kiss* 👨‍🍳`,
          `Nobody: ... Absolutely nobody: ... Me: *obsesses over ${topic}* 🤣`,
          `${topic} is my personality now and I'm not even sorry 😅`,
        ],
        inspirational: [
          `${topic} taught me that growth happens outside your comfort zone 🌱`,
          `Every day is a chance to embrace ${topic} and create something beautiful ✨`,
          `${topic}: Where passion meets purpose. Keep pushing forward 💪`,
          `The journey of ${topic} reminds us that small steps lead to big changes 🌟`,
          `${topic} is proof that when you follow your dreams, magic happens ✨`,
        ],
      };

      const selectedTemplates = templates[tone as keyof typeof templates] || templates.professional;
      setCaptions(selectedTemplates);
      setIsGenerating(false);
      setShowEmailPrompt(true);
    }, 1500);
  };

  const copyCaptionToClipboard = (caption: string) => {
    navigator.clipboard.writeText(caption);
    // You could add a toast notification here
  };

  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#e4f4ff]/20 via-white to-[#b3d9ff]/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <a
            href="/tools"
            className="inline-flex items-center gap-2 text-[#22577A] font-medium mb-6 hover:text-[#38a3a5] transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Tools
          </a>

          <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
            <i className="ri-quill-pen-line mr-2"></i>
            AI Caption Generator
          </div>

          <h1 className="text-5xl lg:text-6xl font-bold text-[#22577A] mb-6">
            Generate Perfect
            <span className="block text-[#38a3a5]">Social Media Captions</span>
          </h1>

          <p className="text-xl text-gray-700 mb-8">
            Create engaging, on-brand captions in seconds with our AI-powered tool
          </p>
        </div>
      </section>

      {/* Generator Tool */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#e4f4ff]/30 to-white rounded-3xl p-8 shadow-xl border-2 border-[#b3d9ff]">
            {/* Input Section */}
            <div className="space-y-6 mb-8">
              {/* Topic Input */}
              <div>
                <label className="block text-sm font-semibold text-[#22577A] mb-2">
                  What's your post about?
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="E.g., coffee, fitness, travel, new product launch..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#66b3ff] focus:outline-none text-gray-800"
                />
              </div>

              {/* Tone Selection */}
              <div>
                <label className="block text-sm font-semibold text-[#22577A] mb-2">
                  Choose your tone
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {tones.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => setTone(t.value)}
                      className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                        tone === t.value
                          ? 'border-[#22577A] bg-[#e4f4ff]'
                          : 'border-gray-200 hover:border-[#66b3ff]'
                      }`}
                    >
                      <i className={`${t.icon} text-2xl text-[#22577A]`}></i>
                      <span className="text-sm font-medium text-gray-800">{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Platform Selection */}
              <div>
                <label className="block text-sm font-semibold text-[#22577A] mb-2">
                  Select platform
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {platforms.map((p) => (
                    <button
                      key={p.value}
                      onClick={() => setPlatform(p.value)}
                      className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                        platform === p.value
                          ? 'border-[#22577A] bg-[#e4f4ff]'
                          : 'border-gray-200 hover:border-[#66b3ff]'
                      }`}
                    >
                      <i className={`${p.icon} text-2xl text-[#22577A]`}></i>
                      <span className="text-sm font-medium text-gray-800">{p.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={generateCaptions}
                disabled={!topic.trim() || isGenerating}
                className="w-full py-4 bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white font-semibold rounded-lg hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {isGenerating ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Generating Captions...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-sparkling-line"></i>
                    Generate Captions
                  </span>
                )}
              </button>
            </div>

            {/* Results Section */}
            {captions.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-[#22577A] mb-4">
                  Your Generated Captions:
                </h3>
                {captions.map((caption, index) => (
                  <div
                    key={index}
                    className="bg-white p-4 rounded-lg border-2 border-gray-200 hover:border-[#66b3ff] transition-all group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <p className="text-gray-800 flex-1">{caption}</p>
                      <button
                        onClick={() => copyCaptionToClipboard(caption)}
                        className="flex items-center gap-2 px-4 py-2 bg-[#e4f4ff] text-[#22577A] rounded-lg font-medium hover:bg-[#b3d9ff] transition-all"
                      >
                        <i className="ri-file-copy-line"></i>
                        Copy
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Email Prompt */}
          {showEmailPrompt && (
            <div className="mt-8 bg-gradient-to-br from-[#22577A] to-[#38a3a5] rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-3">
                Want unlimited caption generations?
              </h3>
              <p className="text-white/90 mb-6">
                Sign up for MandaPost and get access to unlimited AI-powered captions, scheduling, and more!
              </p>
              <a
                href="/signup"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#22577A] rounded-lg font-semibold hover:shadow-xl hover:scale-105 transition-all"
              >
                <i className="ri-rocket-line"></i>
                Start Free Trial
              </a>
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-b from-white to-[#e4f4ff]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#22577A] mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Create perfect captions in 3 simple steps</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#e4f4ff] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-edit-line text-3xl text-[#22577A]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-2">1. Enter Your Topic</h3>
              <p className="text-gray-600">
                Tell us what your post is about in a few words
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e4f4ff] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-palette-line text-3xl text-[#22577A]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-2">2. Choose Your Style</h3>
              <p className="text-gray-600">
                Select the tone and platform for your caption
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e4f4ff] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-sparkling-line text-3xl text-[#22577A]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#22577A] mb-2">3. Get Perfect Captions</h3>
              <p className="text-gray-600">
                Copy your favorite caption and post instantly
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
